import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { PersonTag } from '../../types/person.js';
import * as personApi from '../../services/person-api.js';

@customElement('cp-person-tag-input')
export class CpPersonTagInput extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) personId = 0;
  @property({ type: Array }) tags: PersonTag[] = [];

  @state() private _inputValue = '';
  @state() private _saving = false;

  private async _addTag(): Promise<void> {
    const tag = this._inputValue.trim();
    if (!tag) return;
    if (this.tags.some(t => t.tag === tag.toLowerCase())) {
      this._inputValue = '';
      return;
    }
    this._saving = true;
    try {
      await personApi.addTag(this.personId, tag);
      this._inputValue = '';
      this._notifyUpdated();
    } catch (e) {
      console.error('Failed to add tag:', e);
    } finally {
      this._saving = false;
    }
  }

  private async _removeTag(tag: string): Promise<void> {
    this._saving = true;
    try {
      await personApi.deleteTag(this.personId, tag);
      this._notifyUpdated();
    } catch (e) {
      console.error('Failed to remove tag:', e);
    } finally {
      this._saving = false;
    }
  }

  private _handleKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      void this._addTag();
    }
  }

  private _notifyUpdated(): void {
    this.dispatchEvent(new CustomEvent('tags-updated', { bubbles: true, composed: true }));
  }

  render() {
    return html`
      <div class="cp-person-tags">
        <h3 class="cp-person-tags__title">Tags</h3>
        <div class="cp-person-tags__list">
          ${this.tags.map(t => html`
            <span class="cp-person-tag">
              ${t.tag}
              <button
                class="cp-person-tag__remove"
                @click=${() => void this._removeTag(t.tag)}
                ?disabled=${this._saving}
                title="Tag entfernen"
                aria-label="Tag ${t.tag} entfernen"
              >×</button>
            </span>
          `)}
          ${this.tags.length === 0 ? html`
            <span class="cp-person-tags__empty">Keine Tags vorhanden</span>
          ` : nothing}
        </div>
        <div class="cp-person-tags__input-row">
          <input
            class="cp-person-tags__input"
            type="text"
            placeholder="Tag hinzufügen und Enter drücken"
            .value=${this._inputValue}
            @input=${(e: Event) => { this._inputValue = (e.target as HTMLInputElement).value; }}
            @keydown=${this._handleKeyDown}
            ?disabled=${this._saving}
          />
          <button
            class="cp-person-tags__add-btn"
            @click=${() => void this._addTag()}
            ?disabled=${this._saving || !this._inputValue.trim()}
          >+</button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-person-tag-input': CpPersonTagInput;
  }
}
